import React, { useState, useEffect } from 'react';
import {
  MdArrowBack, MdStars, MdFlight, MdShoppingCart, MdLocalCafe,
  MdAccountBalanceWallet, MdRedeem, MdTrendingUp, MdEco,
} from 'react-icons/md';
import { useApp } from '../contexts/AppContext';
import { useProfile } from '../contexts/ProfileContext';
import { fetchSmartSavings } from '../services/backendAPI';

const TIERS = [
  { id: 'silver', label: 'Silver',   min: 0,      max: 25000,  color: '#9CA3AF' },
  { id: 'gold',   label: 'Gold',     min: 25000,  max: 75000,  color: '#CFBD91' },
  { id: 'plat',   label: 'Platinum', min: 75000,  max: 150000, color: '#002B1A' },
];

const REWARDS = [
  { id: 'flight',  Icon: MdFlight,        label: 'Flight credit',         cost: 35000, sub: 'Toward any airline', tint: 'var(--td-blue)'    },
  { id: 'gift',    Icon: MdShoppingCart,  label: '$100 gift card',        cost: 18000, sub: 'Amazon, Starbucks, more', tint: 'var(--td-amber)' },
  { id: 'cash',    Icon: MdAccountBalanceWallet, label: 'Statement credit', cost: 10000, sub: '$50 off your next bill', tint: 'var(--td-green)' },
  { id: 'coffee',  Icon: MdLocalCafe,     label: 'Coffee for a month',    cost: 5000,  sub: 'Tim Hortons or Starbucks', tint: 'var(--td-brown)' },
];

const ACTIVITY = [
  { id: 'a1', label: 'Grocery purchase',  pts: 240, when: 'Today' },
  { id: 'a2', label: 'Gas — Petro-Canada', pts: 120, when: 'Yesterday' },
  { id: 'a3', label: 'Birthday bonus',    pts: 1000, when: 'May 1' },
  { id: 'a4', label: 'Streaming bill',    pts: 32,  when: 'Apr 30' },
];

const RewardsScreen = ({ onNavigate, onBack }) => {
  const { sessionId } = useApp();
  const { loadedProfileData } = useProfile();

  // Per-persona rewards block (plan 110). Falls back to the demo defaults when
  // the loaded profile has no rewards data (or no profile is loaded at all).
  const rewards = loadedProfileData?.profile?.rewards ?? loadedProfileData?.rewards ?? null;
  const points = rewards?.points ?? 42850;
  const activity = Array.isArray(rewards?.recentEarning) && rewards.recentEarning.length
    ? rewards.recentEarning.map((a, i) => ({ id: `re${i}`, ...a }))
    : ACTIVITY;
  // Smart savings tally (plan 250) — cumulative dollars/CO₂/points/streak from the
  // customer's logged smarter choices, replayed server-side from the ledger.
  const [smart, setSmart] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!sessionId) return undefined;
    fetchSmartSavings(sessionId).then((d) => {
      if (alive && d?.ready && d.total) setSmart(d.total);
    });
    return () => { alive = false; };
  }, [sessionId]);

  const tier = TIERS.find(t => points >= t.min && points < t.max) || TIERS[TIERS.length - 1];
  const next = TIERS[TIERS.indexOf(tier) + 1];
  const progress = next ? Math.min(1, (points - tier.min) / (next.min - tier.min)) : 1;

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero dark">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back to home">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">TD Rewards</h1>
        </div>
        <MdStars size={22} style={{ color: 'var(--td-gold)' }} />
      </header>

      <div className="bh-flow-body">
        <div
          style={{
            background: 'linear-gradient(135deg, var(--td-premium) 0%, #0a3a26 100%)',
            color: 'var(--bh-white)',
            padding: 'var(--space-lg)',
            borderRadius: 'var(--radius-md)',
            display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)',
            position: 'relative', overflow: 'hidden',
          }}
        >
          <div style={{
            position: 'absolute', top: -40, right: -40, width: 160, height: 160,
            background: 'radial-gradient(circle, var(--td-gold) 0%, transparent 70%)',
            opacity: 0.18, pointerEvents: 'none',
          }} />
          <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', opacity: 0.7 }}>
            Available points
          </div>
          <div style={{ font: 'var(--bh-display)', fontWeight: 800, color: 'var(--td-gold)', letterSpacing: '-0.02em' }}>
            {points.toLocaleString('en-CA')}
          </div>
          <div style={{ font: 'var(--bh-body-sm)', opacity: 0.85 }}>
            ≈ ${(points / 200).toFixed(0)} cash value
          </div>

          <div style={{ marginTop: 'var(--space-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', font: 'var(--bh-caption)', opacity: 0.85, marginBottom: 6 }}>
              <span>{tier.label} tier</span>
              <span>{next ? `${(next.min - points).toLocaleString('en-CA')} pts to ${next.label}` : 'Top tier'}</span>
            </div>
            <div style={{
              width: '100%', height: 8, background: 'rgba(255,255,255,0.16)',
              overflow: 'hidden', borderRadius: 'var(--radius-full)',
            }}>
              <div style={{
                width: `${progress * 100}%`, height: '100%',
                background: 'var(--td-gold)',
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        </div>

        {smart && smart.count > 0 && (
          <section>
            <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Smart savings</h2>
            <div className="bh-form-card" style={{ gap: 'var(--space-sm)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <span style={{
                  width: 40, height: 40, background: 'var(--td-green)', color: 'var(--bh-white)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, borderRadius: 'var(--radius-md)',
                }}>
                  <MdEco size={22} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: 'var(--bh-title)', fontWeight: 800, color: 'var(--bh-black)' }}>
                    ${Number(smart.totalSaved || 0).toLocaleString('en-CA', { maximumFractionDigits: 0 })} saved
                  </div>
                  <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                    from {smart.count} smarter choice{smart.count !== 1 ? 's' : ''} · estimated
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-sm)', marginTop: 4 }}>
                {[
                  { k: 'pts', label: 'Points', value: `+${Number(smart.points || 0).toLocaleString('en-CA')}` },
                  { k: 'co2', label: 'CO₂ saved', value: `${Number(smart.totalCo2 || 0).toLocaleString('en-CA')} kg` },
                  { k: 'streak', label: 'Day streak', value: `${Number(smart.streakDays || 0)}` },
                ].map((s) => (
                  <div key={s.k} style={{
                    background: 'var(--bh-light-gray)', borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-sm)', textAlign: 'center',
                  }}>
                    <div style={{ font: 'var(--bh-body)', fontWeight: 800, color: 'var(--td-green)' }}>{s.value}</div>
                    <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Redeem points</h2>
          <div className="bh-action-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-sm)' }}>
            {REWARDS.map(r => {
              const can = points >= r.cost;
              return (
                <button
                  key={r.id}
                  type="button"
                  className="bh-action-card"
                  disabled={!can}
                  style={{ opacity: can ? 1 : 0.55 }}
                  onClick={() => { /* future: confirm-redemption flow */ }}
                >
                  <div className="bh-action-card-icon" style={{ background: r.tint, color: 'var(--bh-white)' }}>
                    <r.Icon size={20} />
                  </div>
                  <span style={{ font: 'var(--bh-body-sm)', fontWeight: 700, color: 'var(--bh-black)' }}>{r.label}</span>
                  <span style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{r.sub}</span>
                  <span style={{
                    marginTop: 4,
                    font: 'var(--bh-caption)', fontWeight: 700,
                    color: can ? 'var(--td-green)' : 'var(--bh-gray)',
                  }}>
                    {r.cost.toLocaleString('en-CA')} pts
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>Recent earning</h2>
          <div className="bh-form-card" style={{ padding: 0, gap: 0 }}>
            {activity.map((a, i) => (
              <div
                key={a.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                  padding: 'var(--space-sm)',
                  borderBottom: i === activity.length - 1 ? 'none' : '1px solid var(--bh-light-gray)',
                }}
              >
                <span style={{
                  width: 36, height: 36, background: 'var(--td-green)', color: 'var(--bh-white)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, borderRadius: 'var(--radius-md)',
                }}>
                  <MdTrendingUp size={18} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: 'var(--bh-body)', fontWeight: 600, color: 'var(--bh-black)' }}>{a.label}</div>
                  <div style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>{a.when}</div>
                </div>
                <div style={{ font: 'var(--bh-body)', fontWeight: 700, color: 'var(--td-green)' }}>
                  +{a.pts.toLocaleString('en-CA')} pts
                </div>
              </div>
            ))}
          </div>
        </section>

        <button
          className="bh-btn-gold"
          onClick={() => { /* future: redeem catalog */ }}
        >
          <MdRedeem size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Browse full catalog
        </button>
      </div>
    </div>
  );
};

export default RewardsScreen;
